import { SidebarInset, SidebarProvider, SidebarTrigger } from '@repo/ui'
import { Outlet, createFileRoute, redirect, useLoaderData } from '@tanstack/react-router'
import type { Project } from '@/components/AppSidebar'
import { AppSidebar } from '@/components/AppSidebar'
import { DocumentArtifactMutationOwnershipProvider } from '@/components/documentAgent/DocumentArtifactMutationOwnership'
import { getOrgUsers } from '@/api/org/users'
import { getProjects } from '@/api/project/list'
import { isConversationWorkspaceAvailable } from '@/features/conversation/availability'
import { ConversationWorkspaceShell } from '@/features/shell/ConversationWorkspaceShell'
import { ProfileSettingsDialogHost } from '@/features/settings/ProfileSettingsDialogHost'
import { ScopedSettingsProvider } from '@/features/settings/ScopedSettings'

export const Route = createFileRoute('/_auth/$orgSlug')({
  beforeLoad: ({ context, params, location }) => {
    // Sysadmins may enter their own org app; the org-mismatch guard below still
    // restricts them (and everyone else) to their own org slug.
    const userSlug = context.user.userOrgProfile?.org.slug
    if (!userSlug) {
      // Org-less users (e.g. a bootstrap sysadmin) have no org app to view; send
      // them home, which routes sysadmins on to /admin.
      throw redirect({ to: '/' })
    }
    if (params.orgSlug !== userSlug) {
      throw redirect({ to: '/org-mismatch', search: { redirect: location.href } })
    }
  },
  loader: async ({ context }) => {
    const uid = context.user.userOrgProfile?.uid

    const [allProjects, orgUsers] = await Promise.all([getProjects(), getOrgUsers()])

    const myProjects: Array<Project> = allProjects
      .filter((p) => p.members.length === 1 && p.members[0]?.userId === uid)
      .map((p) => ({ id: p.projectId, name: p.title, lastUpdated: p.updatedOnTsMs }))

    const sharedProjects: Array<Project> = allProjects
      .filter((p) => p.members.length > 1)
      .map((p) => ({ id: p.projectId, name: p.title, lastUpdated: p.updatedOnTsMs }))

    return {
      allProjects,
      myProjects,
      sharedProjects,
      orgUsers
    }
  },
  component: OrgLayout
})

function OrgLayout() {
  const { orgSlug } = Route.useParams()
  const { organizations } = useLoaderData({ from: '/_auth' })
  const { allProjects, myProjects, sharedProjects, orgUsers } = useLoaderData({ from: Route.id })
  const { userOrgProfile } = Route.useRouteContext().user
  const conversationWorkspaceAvailable = isConversationWorkspaceAvailable(userOrgProfile?.enabled_features)

  const shell =
    conversationWorkspaceAvailable && userOrgProfile !== undefined ? (
      <ConversationWorkspaceShell
        orgId={userOrgProfile.org.org_id}
        currentOrgSlug={orgSlug}
        organizations={organizations}
        orgUsers={orgUsers}
        userOrgProfile={userOrgProfile}
        projects={allProjects}
      >
        <Outlet />
      </ConversationWorkspaceShell>
    ) : (
      <SidebarProvider>
        <AppSidebar
          organizations={organizations}
          myProjects={myProjects}
          sharedProjects={sharedProjects}
          currentOrgSlug={orgSlug}
          contentConnectorsEnabled={userOrgProfile?.enabled_features.content_connectors ?? false}
          globalContentEnabled={userOrgProfile?.enabled_features.global_content ?? false}
          orgUsers={orgUsers}
        />
        <SidebarTrigger floating />
        <SidebarInset>
          <Outlet />
        </SidebarInset>
      </SidebarProvider>
    )

  return (
    <DocumentArtifactMutationOwnershipProvider>
      <ScopedSettingsProvider key={orgSlug}>
        <ProfileSettingsDialogHost />
        {shell}
      </ScopedSettingsProvider>
    </DocumentArtifactMutationOwnershipProvider>
  )
}
